import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Tooltip,
  InputAdornment,
  GlobalStyles,
} from "@mui/material";
import {
  AccountBalanceWalletOutlined,
  ReceiptLongOutlined,
  TaskAltOutlined,
  HourglassTopOutlined,
  OpenInNewOutlined,
  PaymentsOutlined,
  CloseOutlined,
  InsightsOutlined,
  BoltOutlined,
  AutoGraphOutlined,
  PhoneIphoneOutlined,
  RefreshOutlined,
} from "@mui/icons-material";
import { useTheme } from "@emotion/react";
import { tokens } from "../../../theme";
import DynamicTableForJson from "../../../data/DynamicTable/dynamicTableForJson";
import { customerservice } from "../../../config";
import { fetchJsonDynamic } from "../../../data/DynamicServiceNew/jsondynamicservice";
import {
  mpesaPayFinsights,
  fetchFinsightsTokenUsage,
} from "../../../data/DynamicTable/DynamicFunctions";
import Swal from "sweetalert2";
import UploadFinsightsStatementForm from "./form";

const base_url = customerservice.uri;
const BRAND_BLUE = "#1766A2";
const BRAND_BLUE_BRIGHT = "#2e9be6";
const NEON_GREEN = "#7ad9aa";
const FINSIGHTS_URL = "https://finsights.sababisha.com/";

// ---------------------------------------------------------------------------
// Futuristic motion system (injected once)
// ---------------------------------------------------------------------------
const FuturisticMotion = () => (
  <GlobalStyles
    styles={{
      "@keyframes fsShimmer": {
        "0%": { backgroundPosition: "-200% 0" },
        "100%": { backgroundPosition: "200% 0" },
      },
      "@keyframes fsPulse": {
        "0%, 100%": { boxShadow: "0 0 0 0 rgba(46,155,230,0.45)" },
        "50%": { boxShadow: "0 0 0 8px rgba(46,155,230,0)" },
      },
      "@keyframes fsFadeUp": {
        "0%": { opacity: 0, transform: "translateY(14px)" },
        "100%": { opacity: 1, transform: "translateY(0)" },
      },
      "@keyframes fsScan": {
        "0%": { transform: "translateX(-100%)" },
        "100%": { transform: "translateX(220%)" },
      },
    }}
  />
);

const fadeUp = (delay = 0) => ({
  animation: `fsFadeUp 0.5s ease ${delay}s both`,
});

// Balance comes from a two-step handshake in DynamicFunctions.js:
// fetchFinsightsTokenUsage() first calls our backend's GetFinsightsToken to
// get a short-lived FinSights-issued JWT, then uses that to query the
// external FinSights gateway's `tokenUsageTracker` for the token/credit count.

const highlights = [
  { icon: <BoltOutlined fontSize="inherit" />, title: "Crunched in seconds" },
  {
    icon: <AutoGraphOutlined fontSize="inherit" />,
    title: "Actionable insights",
  },
  {
    icon: <InsightsOutlined fontSize="inherit" />,
    title: "M-Pesa & bank ready",
  },
];

const isCrunchedStatus = (row) => row?.StatementId > 0;

// Shared futuristic surface style
const glassSurface = (isDark) => ({
  borderRadius: "16px",
  border: `1px solid ${
    isDark ? "rgba(46,155,230,0.25)" : "rgba(23,102,162,0.18)"
  }`,
  background: isDark
    ? "linear-gradient(145deg, rgba(23,102,162,0.14), rgba(12,28,44,0.9))"
    : "linear-gradient(145deg, rgba(23,102,162,0.05), #ffffff)",
  backdropFilter: "blur(6px)",
  transition: "box-shadow 0.25s ease, transform 0.25s ease",
  "&:hover": {
    boxShadow: isDark
      ? "0 0 22px rgba(46,155,230,0.25)"
      : "0 8px 24px rgba(23,102,162,0.18)",
    transform: "translateY(-2px)",
  },
});

// ---------------------------------------------------------------------------
// M-Pesa Top-Up Dialog (logic unchanged)
// ---------------------------------------------------------------------------
export const PayFinsightsDialog = ({ open, onClose, onPaid }) => {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [amount, setAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      setPhoneNumber("");
      setAmount("");
      setErrors({});
    }
  }, [open]);

  const validate = () => {
    const nextErrors = {};
    if (!/^[0-9]{9,15}$/.test(phoneNumber.trim())) {
      nextErrors.phoneNumber = "Enter a valid M-Pesa phone number";
    }
    if (!amount || Number(amount) <= 0) {
      nextErrors.amount = "Enter an amount greater than 0";
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handlePay = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const response = await mpesaPayFinsights(
        Number(amount),
        phoneNumber.trim(),
      );
      if (response?.status === 200) {
        Swal.fire({
          icon: "success",
          title: "STK Push Sent!",
          text: "Check your phone to complete the M-Pesa payment. Your FinSights balance will update once the payment is confirmed.",
        });
        onPaid?.();
        onClose();
      } else {
        Swal.fire({
          icon: "error",
          title: "Payment Failed",
          text: "We couldn't initiate the payment. Please try again.",
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Payment Failed",
        text: error?.response?.data?.error || "An unexpected error occurred.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle
        sx={{
          background: `linear-gradient(135deg, ${BRAND_BLUE} 0%, #0a3a5c 100%)`,
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <PaymentsOutlined />
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, fontSize: "1.05rem" }}
          >
            Top Up FinSights Balance
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={onClose}
          disabled={submitting}
          sx={{ color: "#fff" }}
        >
          <CloseOutlined fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 3, mt: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          Pay via M-Pesa STK Push to top up your company's FinSights wallet and
          keep crunching statements without interruption.
        </Typography>
        <Box display="flex" flexDirection="column" gap={2.5}>
          <TextField
            label="M-Pesa Phone Number"
            placeholder="0712345678"
            fullWidth
            value={phoneNumber}
            onChange={(e) =>
              setPhoneNumber(e.target.value.replace(/[^0-9]/g, ""))
            }
            error={!!errors.phoneNumber}
            helperText={errors.phoneNumber}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PhoneIphoneOutlined fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="Amount"
            placeholder="0.00"
            type="number"
            fullWidth
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            error={!!errors.amount}
            helperText={errors.amount}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">KES</InputAdornment>
              ),
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={onClose}
          disabled={submitting}
          sx={{ textTransform: "none" }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handlePay}
          disabled={submitting}
          startIcon={
            submitting ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <PaymentsOutlined />
            )
          }
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: "10px",
            background: BRAND_BLUE,
            "&:hover": { background: "#0f4c73" },
          }}
        >
          {submitting ? "Processing..." : "Pay Now"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Compact horizontal stat tile (icon + value inline, saves vertical space)
// ---------------------------------------------------------------------------
const StatTile = ({ icon, label, value, accent }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Paper
      variant="outlined"
      sx={{
        ...glassSurface(isDark),
        p: 1.75,
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        height: "100%",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* neon edge line */}
      <Box
        sx={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 3,
          background: `linear-gradient(180deg, ${accent}, transparent)`,
          boxShadow: `0 0 8px ${accent}`,
        }}
      />
      <Box
        sx={{
          width: 40,
          height: 40,
          flexShrink: 0,
          borderRadius: "12px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: accent,
          bgcolor: `${accent}1f`,
          border: `1px solid ${accent}40`,
          boxShadow: `0 0 12px ${accent}30`,
        }}
      >
        {icon}
      </Box>
      <Box minWidth={0}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            textTransform: "uppercase",
            letterSpacing: 0.8,
            fontSize: "0.62rem",
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </Typography>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 800,
            lineHeight: 1.1,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {value}
        </Typography>
      </Box>
    </Paper>
  );
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
const Finsights = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";
  const decodedToken = JSON.parse(
    atob(localStorage.getItem("token")?.split(".")[1] || "{}"),
  );
  const companyId = decodedToken?.CompanyId;
  const [balance, setBalance] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, crunched: 0, pending: 0 });
  const [payOpen, setPayOpen] = useState(false);

  const actions = {
    entity: "Finsights",
    isSubright: true,
    add: {
      key: "ADD",
      button_name: "Upload Statement",
      Show_Button: true,
      permission: "CreatePermission",
    },
    dataObject: {
      objectName: "customerStatementsById",
      inputObjectName: "statements",
      parameters: { companyId: parseInt(companyId) },
    },
    ViewStatementReport: {
      button_name: "View Report",
      Show_Button: true,
      permission: "hasNoPermission",
      roleGroupId: [1, 2, 3, 4, 5],
    },
    showStatus: true,
  };

  const loadBalance = async () => {
    setBalanceLoading(true);
    try {
      const record = await fetchFinsightsTokenUsage();
      setBalance(record?.token ?? null);
    } catch (error) {
      setBalance(null);
    } finally {
      setBalanceLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await fetchJsonDynamic(
        actions.dataObject.objectName,
        actions.dataObject.inputObjectName,
        actions.dataObject.parameters,
        base_url,
      );
      const rows = response?.results ? JSON.parse(response.results) : [];
      const total = Array.isArray(rows) ? rows.length : 0;
      const crunched = Array.isArray(rows)
        ? rows.filter(isCrunchedStatus).length
        : 0;
      setStats({ total, crunched, pending: total - crunched });
    } catch (error) {
      setStats({ total: 0, crunched: 0, pending: 0 });
    }
  };

  useEffect(() => {
    loadBalance();
    loadStats();
  }, []);

  return (
    <Box m="20px" display="flex" flexDirection="column" gap={2}>
      <FuturisticMotion />

      {/* ---------- Compact futuristic hero + balance wallet in one row ------ */}
      <Paper
        sx={{
          ...fadeUp(0),
          borderRadius: "18px",
          p: { xs: 2, md: 2.5 },
          position: "relative",
          overflow: "hidden",
          color: "#fff",
          background: `linear-gradient(120deg, #0a3a5c 0%, ${BRAND_BLUE} 55%, #1d7ec2 100%)`,
          border: `1px solid ${BRAND_BLUE_BRIGHT}55`,
          boxShadow: isDark
            ? "0 0 32px rgba(46,155,230,0.28)"
            : "0 12px 32px rgba(23,102,162,0.30)",
          // animated sheen sweep
          "&::after": {
            content: '""',
            position: "absolute",
            top: 0,
            bottom: 0,
            width: "45%",
            background:
              "linear-gradient(100deg, transparent, rgba(255,255,255,0.10), transparent)",
            animation: "fsScan 6s linear infinite",
            pointerEvents: "none",
          },
          // subtle grid texture
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            backgroundImage: `linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)`,
            backgroundSize: "28px 28px",
            pointerEvents: "none",
          },
        }}
      >
        <Box
          display="flex"
          flexWrap="wrap"
          alignItems="center"
          justifyContent="space-between"
          gap={2.5}
          sx={{ position: "relative", zIndex: 1 }}
        >
          {/* Left: brand + highlights as compact chips */}
          <Box sx={{ minWidth: 260, flex: "1 1 320px" }}>
            <Box display="flex" alignItems="center" gap={1}>
              <InsightsOutlined sx={{ color: NEON_GREEN }} />
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  letterSpacing: 0.3,
                  background: `linear-gradient(90deg, #fff 30%, ${NEON_GREEN})`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                FinSights
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{ mt: 0.5, opacity: 0.85, maxWidth: 440 }}
            >
              Upload Mpesa or bank statements, get crunched insights in
              seconds.
            </Typography>

            {/* highlight chips — replaces the old 3 stacked cards */}
            <Box display="flex" flexWrap="wrap" gap={1} mt={1.5}>
              {highlights.map((item) => (
                <Box
                  key={item.title}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    px: 1.25,
                    py: 0.5,
                    borderRadius: "999px",
                    border: "1px solid rgba(255,255,255,0.28)",
                    bgcolor: "rgba(255,255,255,0.08)",
                    backdropFilter: "blur(4px)",
                  }}
                >
                  <Box sx={{ color: NEON_GREEN, display: "flex" }}>
                    {item.icon}
                  </Box>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {item.title}
                  </Typography>
                </Box>
              ))}
              <Box
                component="button"
                onClick={() =>
                  window.open(FINSIGHTS_URL, "_blank", "noopener,noreferrer")
                }
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  px: 1.25,
                  py: 0.5,
                  borderRadius: "999px",
                  border: `1px solid ${NEON_GREEN}80`,
                  bgcolor: "transparent",
                  color: NEON_GREEN,
                  cursor: "pointer",
                  font: "inherit",
                  fontSize: "0.72rem",
                  fontWeight: 600,
                  transition: "background 0.2s",
                  "&:hover": { bgcolor: "rgba(122,217,170,0.12)" },
                }}
              >
                Visit FinSights
                <OpenInNewOutlined sx={{ fontSize: 13 }} />
              </Box>
            </Box>
          </Box>

          {/* Right: token wallet glass card with inline top-up */}
          <Paper
            elevation={0}
            sx={{
              flex: "0 1 auto",
              minWidth: { xs: "100%", sm: 290 },
              borderRadius: "16px",
              p: 2,
              color: "#fff",
              bgcolor: "rgba(255,255,255,0.08)",
              border: "1px solid rgba(255,255,255,0.25)",
              backdropFilter: "blur(10px)",
              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.15)",
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: NEON_GREEN,
                    bgcolor: "rgba(122,217,170,0.15)",
                    border: `1px solid ${NEON_GREEN}55`,
                    animation: "fsPulse 2.6s ease-in-out infinite",
                  }}
                >
                  <AccountBalanceWalletOutlined fontSize="small" />
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    textTransform: "uppercase",
                    letterSpacing: 1,
                    opacity: 0.8,
                  }}
                >
                  FinSights Tokens
                </Typography>
              </Box>
              <Tooltip title="Refresh balance">
                <IconButton
                  size="small"
                  onClick={loadBalance}
                  sx={{
                    color: "rgba(255,255,255,0.85)",
                    "&:hover": { color: "#fff", rotate: "90deg" },
                    transition: "rotate 0.3s",
                  }}
                >
                  <RefreshOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            <Typography
              variant="h4"
              sx={{ fontWeight: 800, mt: 1, color: NEON_GREEN }}
            >
              {balanceLoading ? (
                <CircularProgress size={22} sx={{ color: NEON_GREEN }} />
              ) : balance === null ? (
                "—"
              ) : (
                <>
                  {Number(balance).toLocaleString()}
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{ fontWeight: 500, opacity: 0.75, ml: 0.75 }}
                  >
                    {Number(balance) === 1 ? "Token" : "Tokens"}
                  </Typography>
                </>
              )}
            </Typography>

            <Button
              fullWidth
              variant="contained"
              startIcon={<PaymentsOutlined />}
              onClick={() => setPayOpen(true)}
              sx={{
                mt: 1.5,
                textTransform: "none",
                fontWeight: 700,
                borderRadius: "10px",
                color: "#0a3a5c",
                background: NEON_GREEN,
                boxShadow: "0 0 16px rgba(122,217,170,0.45)",
                "&:hover": {
                  background: "#96e6bd",
                  boxShadow: "0 0 24px rgba(122,217,170,0.65)",
                },
              }}
            >
              Top Up via M-Pesa
            </Button>
          </Paper>
        </Box>
      </Paper>

      {/* ---------- Compact stats row ---------- */}
      <Grid container spacing={1.5} sx={fadeUp(0.1)}>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile
            icon={<ReceiptLongOutlined fontSize="small" />}
            label="Uploaded"
            accent={colors.blueAccent[500]}
            value={stats.total}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile
            icon={<TaskAltOutlined fontSize="small" />}
            label="Crunched"
            accent={colors.greenAccent[500]}
            value={stats.crunched}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile
            icon={<HourglassTopOutlined fontSize="small" />}
            label="Pending"
            accent={colors.redAccent[500]}
            value={stats.pending}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile
            icon={<BoltOutlined fontSize="small" />}
            label="Crunch Rate"
            accent={BRAND_BLUE_BRIGHT}
            value={
              stats.total > 0
                ? `${Math.round((stats.crunched / stats.total) * 100)}%`
                : "—"
            }
          />
        </Grid>
      </Grid>

      {/* ---------- Statements table ---------- */}
      <Box sx={fadeUp(0.2)}>
        <DynamicTableForJson
          title="FinSights Statements"
          subtitle="Statements uploaded and crunched across your customers"
          base_url={base_url}
          actions={actions}
          FormComponent={UploadFinsightsStatementForm}
          onDataChanged={loadStats}
        />
      </Box>

      <PayFinsightsDialog
        open={payOpen}
        onClose={() => setPayOpen(false)}
        onPaid={loadBalance}
      />
    </Box>
  );
};

export default Finsights;